/**
 * Sign-out and session-expiry handling shared by the logout buttons, the axios token
 * interceptors and the pages that ask Auth0 for a token directly.
 */

type Logout = (options?: { returnTo?: string }) => void;

// Auth0 errors meaning the session can't be renewed silently and the user must sign in again
const SESSION_EXPIRED_ERRORS = ["login_required", "consent_required", "invalid_grant", "missing_refresh_token"];

export function isSessionExpiredError(error: any): boolean {
  return SESSION_EXPIRED_ERRORS.includes(error?.error);
}

// Storage keys that belong to the signed-in user's session. Preferences such as filters,
// columns and searches are kept across sign-ins.
const LOCAL_SESSION_KEYS = ["time", "isAuthenticated", "accessToken", "userLoggedIn"];
const SESSION_SESSION_KEYS = ["auth0ProfilePicture"];
// Auth0's own token cache (cacheLocation='localstorage'); logout clears it too, this is a safety net
const AUTH0_CACHE_PREFIX = "@@auth0spajs@@";

/** Removes the signed-in user's session data, keeping the app's stored preferences. */
export function clearSessionData() {
  LOCAL_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  Object.keys(localStorage)
    .filter((key) => key.startsWith(AUTH0_CACHE_PREFIX))
    .forEach((key) => localStorage.removeItem(key));
  SESSION_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
}

let sessionEnding = false;

/** Clears the session data and logs out once, returning to the current site. */
export function endSession(logout: Logout) {
  if (sessionEnding) return;
  sessionEnding = true;
  clearSessionData();
  logout({ returnTo: window.location.origin });
}

/**
 * For code that calls getAccessTokenSilently directly (outside the axios interceptors):
 * signs out when the error means the session is over. Returns true when it did, so the
 * caller can stop; otherwise the caller should show its own error state.
 */
export function endSessionIfExpired(error: unknown, logout: Logout): boolean {
  if (!isSessionExpiredError(error)) return false;
  endSession(logout);
  return true;
}
